import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Container } from '../../layout/Container'
import { MetricCounter } from '../../ui/MetricCounter'
import { useScrollAnimation } from '../../../hooks/useScrollAnimation'

interface MetricItem {
  value: number
  suffix?: string
  prefix?: string
  label: string
  decimals?: number
}

interface MetricsSectionProps {
  tKey: 'brain' | 'build' | 'govern'
}

export function MetricsSection({ tKey }: MetricsSectionProps) {
  const { t } = useTranslation()
  const { ref, isInView } = useScrollAnimation()

  const items = t(`platformPages.${tKey}.metrics.items`, { returnObjects: true }) as MetricItem[]

  return (
    <section ref={ref} className="py-24">
      <Container size="wide">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <span className="text-sm font-medium uppercase tracking-wider text-accent-light">
            {t(`platformPages.${tKey}.metrics.label`)}
          </span>
          <h2 className="mt-4 text-3xl font-semibold tracking-tight text-text-primary sm:text-4xl">
            {t(`platformPages.${tKey}.metrics.title`)}
          </h2>
        </motion.div>

        <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((metric, index) => (
            <motion.div
              key={metric.label}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="rounded-2xl border border-border bg-surface p-8"
            >
              <MetricCounter
                value={metric.value}
                suffix={metric.suffix}
                prefix={metric.prefix}
                label={metric.label}
                decimals={metric.decimals}
              />
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  )
}
